import React, { useMemo } from 'react';
import { Box, Grid, useTheme } from '@mui/material';
import ReviewerCard from './ReviewerCard';
import { useGetSepTasksQuery, useGetSepQuery } from '../../../services/sepSlice';
import { useGetSepStakeholderQuery } from '../../../services/defaultStakeholderSlice';
import { TaskExtended } from '../../../../shared/types/Task';
import { Department } from '../../../../shared/types/Department';

interface AssignedReviewTeamProps {
  sepID: number;
}

interface ReviewTeamMember {
  reviewer: any;
  department: Department | null;
  isRequester: boolean;
}

const AssignedReviewTeam: React.FC<AssignedReviewTeamProps> = ({ sepID }) => {
  const theme = useTheme();

  const { data: tasks, isLoading: tasksLoading } = useGetSepTasksQuery(sepID);
  const { data: stakeHolders, isLoading: stakeholdersLoading } = useGetSepStakeholderQuery(sepID);
  const sepData = useGetSepQuery(sepID);

  const isReviewTeamLoading = tasksLoading || stakeholdersLoading;

  const reviewTeam = useMemo<ReviewTeamMember[]>(() => {
    // Don't build partial results while data is still loading; this avoids
    // showing only the requester for a moment before the rest pop in.
    if (isReviewTeamLoading) {
      return [];
    }

    const enabledTasks: TaskExtended[] =
      tasks?.filter((e: any) => e.enabled) || [];

    const uniqueDepartmentIds = new Set(
      enabledTasks?.map((task) => task?.department?.id)
    );

    const reviewers: ReviewTeamMember[] =
      stakeHolders
        ?.filter(
          (stakeHolder) =>
            uniqueDepartmentIds.has(stakeHolder.departmentID) &&
            !!stakeHolder.user
        )
        .map((stakeHolder) => ({
          reviewer: stakeHolder.user,
          department: stakeHolder.department,
          isRequester: false,
        })) || [];

    if (sepData?.data?.creator) {
      reviewers.unshift({
        reviewer: sepData.data.creator,
        department: null,
        isRequester: true,
      });
    }

    return reviewers;
  }, [tasks, stakeHolders, sepData?.data?.creator, isReviewTeamLoading]);

  if (reviewTeam.length === 0) {
    return null;
  }

  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        mt: { xs: 2, md: 3 },
        mb: { xs: 1.5, md: 2 },
        width: '100%',
        backgroundColor: isDarkMode
          ? 'rgba(255, 255, 255, 0.04)'
          : theme.palette.gray.lightGray2,
        // Responsive padding: tighter on small screens, roomier on wide screens
        padding: {
          xs: '16px 12px',
          sm: '20px 20px',
          md: '20px 24px',
          lg: '24px 32px',
          xl: '28px 40px',
        },
        borderRadius: '4px',
        border: `1px solid ${
          isDarkMode ? 'rgba(255, 255, 255, 0.12)' : theme.palette.gray.lightGray1
        }`,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          // Responsive column count: 1 -> 2 -> 3 -> 4 -> 5 as screen widens
          gridTemplateColumns: {
            xs: 'repeat(1, minmax(0, 1fr))',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(3, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))',
            xl: 'repeat(5, minmax(0, 1fr))',
          },
          columnGap: { xs: '12px', md: '16px', lg: '20px', xl: '24px' },
          rowGap: { xs: '16px', md: '20px', lg: '24px', xl: '30px' },
          width: '100%',
        }}
      >
        {reviewTeam.map((team, idx) => (
          <ReviewerCard
            key={`reviewTeam${idx}`}
            reviewer={team.reviewer}
            department={team.department}
            isRequester={team.isRequester}
          />
        ))}
      </Box>
    </Box>
  );
};

export default AssignedReviewTeam;
